import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import SupervisorNav from '../../components/supervisor/SupervisorNav'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import EmptyState from '../../components/shell/EmptyState'
import DetailForm, { type DetailFormValues } from '../../components/supervisor/DetailForm'
import ScheduleDefaultForm, { type ScheduleDefaultFormValues } from '../../components/supervisor/ScheduleDefaultForm'
import TransferForm, { type TransferFormValues } from '../../components/supervisor/TransferForm'
import { CATEGORY_FILL } from '../../components/agent/scheduleDay'
import { supabase } from '../../lib/supabase'
import { formatScheduleLabel, useTimeFormat } from '../../lib/timeFormat'
import { PAY_PERIOD, PAY_PERIOD_DATES } from '../agent/payPeriod19'
import { PAY_PERIOD_20_DATES } from '../agent/payPeriod20'
import type { DayEntry } from '../../components/agent/scheduleDay'
import { removeEmployeeFromDraft } from './planningBoardDraftStore'
import { effectiveStatusFor, firstDayAfterDetailEnds } from './personnelDirectory'
import {
  getAssignmentHistory,
  getEmployeeSchedule,
  getPersonnel,
  getTransferDestinations,
  transferEmployees,
  type AssignmentHistoryItem,
  type PersonnelPerson,
  type TransferDestination,
} from './personnelStore'
import { createDetails, getPersonnelDetails, type PersonnelDetail } from './personnelDetailStore'
import {
  getScheduleDefault,
  rebuildScheduleDefault as rebuildStoredScheduleDefault,
  setScheduleDefaultWeekday,
  type EmployeeScheduleDefault,
} from './scheduleDefaultStore'

/**
 * docs/design/journey-map.md's "Personnel directory": a row opens the
 * profile, which carries the schedule defaults, the effective-dated
 * assignment history, details, and the current period's schedule.
 *
 * Schedule defaults read the employee's own week (scheduleDefaultStore.ts's
 * getScheduleDefault): each weekday a supervisor has set, laid over the
 * employee's shift template, per docs/product/scheduling-model.md's "until
 * their own defaults are set." Rebuild from shift template clears every
 * weekday back to it; Edit a weekday sets one weekday, a workday with or
 * without an assignment or a regular day off, through the same type-to-enter
 * code catalog SupervisorPlanningBoardPage.tsx's own cell editor uses
 * (ScheduleDefaultForm.tsx, boardCodes.ts). A change applies from the next
 * unpublished pay period per that same section: it never rewrites the
 * current period's own real day-by-day schedule below.
 *
 * The current period's schedule is that person's own schedule_entries
 * (personnelStore.ts's getEmployeeSchedule), the same real days the planning
 * board shows for them, so a change made there is reflected here without a
 * second copy of the schedule to keep in sync.
 *
 * Add a detail creates one detail for this employee
 * (personnelDetailStore.ts's createDetails, through v1-create-details);
 * SupervisorPersonnelDirectoryPage.tsx's own bulk form is the same
 * component applying the same shape to several people at once. A detail
 * only marks the employee Detailed while today falls within its own dates
 * (personnelDirectory.ts's effectiveStatusFor).
 *
 * Transfer moves this employee to another team from an effective date
 * (personnelStore.ts's transferEmployees, through v1-transfer-employees),
 * which closes their current placement and opens the next; Assignment
 * history reads those placements back, so a transfer adds its own entry.
 * The effective date's own earliest choice moves to Pay Period 20's start
 * while this employee holds an active detail, per
 * docs/product/scheduling-model.md's "the earliest effective date for a
 * transfer is the first day of the pay period after the detail ends," an
 * approximation since a detail's own end date is not otherwise checked
 * against a period boundary.
 */

function ArrowLeftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  )
}

export default function SupervisorPersonnelProfilePage() {
  const identity = useProfileIdentity()
  const { format } = useTimeFormat()
  const { id } = useParams<{ id: string }>()
  const [person, setPerson] = useState<PersonnelPerson | undefined>(undefined)
  const [days, setDays] = useState<DayEntry[]>([])
  const [history, setHistory] = useState<AssignmentHistoryItem[]>([])
  const [destinations, setDestinations] = useState<TransferDestination[]>([])
  const [userId, setUserId] = useState<string | null>(null)
  const [supervisorName, setSupervisorName] = useState<string | null>(null)
  const [sessionReady, setSessionReady] = useState(false)
  const [details, setDetails] = useState<PersonnelDetail[]>([])
  const [formOpen, setFormOpen] = useState(false)
  const [detailFailed, setDetailFailed] = useState(false)
  const [transferFormOpen, setTransferFormOpen] = useState(false)
  const [transferFailed, setTransferFailed] = useState(false)
  const [scheduleDefault, setScheduleDefault] = useState<EmployeeScheduleDefault>({ shift: null, weekly: [] })
  const [weekdayFormOpen, setWeekdayFormOpen] = useState(false)
  const [scheduleDefaultFailed, setScheduleDefaultFailed] = useState(false)

  // Waits on the profile fetch too, rather than resolving sessionReady as
  // soon as the session itself does, the same race
  // SupervisorApprovalInboxPage.tsx's own sessionReady closes for
  // supervisorName: by the time the profile is interactive, Add a detail
  // already has a real identity to record it under.
  useEffect(() => {
    let cancelled = false
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (cancelled) return
        const sessionId = session?.user.id
        if (sessionId) {
          setUserId(sessionId)
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', sessionId)
            .single()
          const name = profile ? `${profile.first_name} ${profile.last_name}` : ''
          if (!cancelled && profile) setSupervisorName(name)
          const [people, loadedDestinations, loadedDetails] = await Promise.all([
            getPersonnel(sessionId, name),
            getTransferDestinations(),
            getPersonnelDetails(),
          ])
          const found = people.find((candidate) => candidate.identifier === id)
          if (found) {
            const [loadedHistory, loadedDays, loadedDefault] = await Promise.all([
              getAssignmentHistory(found.id),
              getEmployeeSchedule(found.id, PAY_PERIOD_DATES),
              getScheduleDefault(found.id),
            ])
            if (!cancelled) {
              setPerson(found)
              setHistory(loadedHistory)
              setDays(loadedDays)
              setScheduleDefault(loadedDefault)
            }
          }
          if (!cancelled) {
            setDestinations(loadedDestinations)
            setDetails(loadedDetails)
          }
        }
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  async function saveDetail(values: DetailFormValues) {
    if (!person) {
      setDetailFailed(true)
      return
    }
    if (
      !(await createDetails({
        employeeIds: [person.id],
        detailType: values.type,
        destination: values.destination,
        startDate: values.startDate,
        endDate: values.endDate,
      }))
    ) {
      setDetailFailed(true)
      return
    }
    setDetailFailed(false)
    setDetails(await getPersonnelDetails())
    setFormOpen(false)
  }

  async function saveTransfer(values: TransferFormValues) {
    if (!userId || !person) {
      setTransferFailed(true)
      return
    }
    if (!(await transferEmployees({ employeeIds: [person.id], ...values }))) {
      setTransferFailed(true)
      return
    }
    setTransferFailed(false)
    // Their unsaved planning board edits on the team they leave name a
    // schedule they no longer keep there.
    removeEmployeeFromDraft(userId, person.teamId, person.id)
    // Reloaded rather than patched: a transfer effective later leaves the
    // employee here today, and one effective already moves them off.
    const reloaded = (await getPersonnel(userId, supervisorName ?? '')).find((candidate) => candidate.id === person.id)
    setPerson(reloaded)
    setHistory(await getAssignmentHistory(person.id))
    setTransferFormOpen(false)
  }

  async function rebuildScheduleDefault() {
    if (!person || !(await rebuildStoredScheduleDefault(person.id))) {
      setScheduleDefaultFailed(true)
      return
    }
    setScheduleDefaultFailed(false)
    setScheduleDefault(await getScheduleDefault(person.id))
  }

  async function saveScheduleDefaultWeekday(values: ScheduleDefaultFormValues) {
    if (!userId || !person || !(await setScheduleDefaultWeekday(userId, person.id, values))) {
      setScheduleDefaultFailed(true)
      return
    }
    setScheduleDefaultFailed(false)
    setScheduleDefault(await getScheduleDefault(person.id))
    setWeekdayFormOpen(false)
  }

  const { status, detailLabel, detailEndDate } = person
    ? effectiveStatusFor(person, details)
    : { status: 'active' as const, detailLabel: undefined, detailEndDate: undefined }
  const otherTeams = person ? destinations.filter((team) => team.id !== person.teamId) : []
  const minTransferDate =
    status === 'detailed' ? (detailEndDate ? firstDayAfterDetailEnds(detailEndDate) : PAY_PERIOD_20_DATES[0]?.date) : undefined
  const weeklyDefault = scheduleDefault.weekly

  if (!sessionReady) {
    return (
      <div className="flex h-dvh flex-col bg-surface text-ink">
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-4">
          <p role="status" className="text-small text-ink-muted">
            Loading&hellip;
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col bg-surface text-ink">
      <a
        href="#personnel-profile-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="personnel-profile-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto p-4 sm:p-6 lg:p-8">
        <SupervisorNav />
        <div className="mx-auto flex w-full max-w-[1100px] flex-1 flex-col gap-5">
          <Link
            to="/supervisor/personnel"
            className="flex min-h-11 items-center gap-1.5 self-start rounded-control text-small font-bold text-ink-muted"
          >
            <ArrowLeftIcon />
            Directory
          </Link>

          {!person ? (
            <EmptyState>No personnel record matches that identifier.</EmptyState>
          ) : (
            <>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="text-h2 font-extrabold">{person.name}</h1>
                  <p className="mt-1 text-small text-ink-muted">
                    {person.title} &middot; {person.identifier} &middot; {person.team} &middot; {person.fieldOffice}
                  </p>
                </div>
                <span
                  className={`rounded-full px-2.5 py-1 text-caption font-bold ${
                    status === 'detailed' ? 'bg-info/10 text-info' : 'bg-success/10 text-success'
                  }`}
                >
                  {status === 'detailed' ? 'Detailed' : 'Active'}
                </span>
              </div>

              {otherTeams.length > 0 && (
                <section className="rounded-surface border border-line bg-canvas p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-small font-bold">Team</h2>
                    <button
                      type="button"
                      aria-expanded={transferFormOpen}
                      onClick={() => setTransferFormOpen((open) => !open)}
                      className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-caption font-bold hover:bg-surface-2"
                    >
                      Transfer
                    </button>
                  </div>
                  <p className="mt-1 text-small">{person.team}</p>
                  {transferFailed && (
                    <p role="alert" className="mt-1 text-caption font-semibold text-error">
                      Could not make the transfer. Check the date and try again.
                    </p>
                  )}
                  {transferFormOpen && (
                    <div className="mt-3">
                      <TransferForm
                        destinations={otherTeams}
                        minEffectiveDate={minTransferDate}
                        onSave={saveTransfer}
                        onCancel={() => setTransferFormOpen(false)}
                      />
                    </div>
                  )}
                </section>
              )}

              <section className="rounded-surface border border-line bg-canvas p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-small font-bold">Schedule defaults</h2>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={rebuildScheduleDefault}
                      className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-caption font-bold hover:bg-surface-2"
                    >
                      Rebuild from shift template
                    </button>
                    <button
                      type="button"
                      aria-expanded={weekdayFormOpen}
                      onClick={() => setWeekdayFormOpen((open) => !open)}
                      className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-caption font-bold hover:bg-surface-2"
                    >
                      Edit a weekday
                    </button>
                  </div>
                </div>
                <p className="mt-1 text-caption text-ink-muted">
                  {scheduleDefault.shift
                    ? `${scheduleDefault.shift.name} · ${formatScheduleLabel(scheduleDefault.shift.time, format)} · ${scheduleDefault.shift.appliesTo}`
                    : 'No shift template'}
                </p>
                {scheduleDefaultFailed && (
                  <p role="alert" className="mt-1 text-caption font-semibold text-error">
                    Could not save the change. Try again.
                  </p>
                )}
                {weekdayFormOpen && (
                  <div className="mt-3">
                    <ScheduleDefaultForm onSave={saveScheduleDefaultWeekday} onCancel={() => setWeekdayFormOpen(false)} />
                  </div>
                )}
                <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {weeklyDefault.map((day) => (
                    <li key={day.weekday} className={`rounded-control px-2 py-1.5 text-caption ${CATEGORY_FILL[day.category]}`}>
                      <p className="font-bold">{day.weekday}</p>
                      <p className="text-ink-muted">{day.category === 'off' ? 'Regular day off' : (day.assignment ?? 'Scheduled workday')}</p>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="rounded-surface border border-line bg-canvas p-4">
                <h2 className="text-small font-bold">Assignment history</h2>
                <ul className="mt-2 flex flex-col gap-1.5">
                  {history.map((entry) => (
                    <li key={`${entry.effectiveDate}-${entry.change}`} className="text-small">
                      <span className="font-bold">Effective {entry.effectiveDate}:</span> {entry.change}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="rounded-surface border border-line bg-canvas p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-small font-bold">Details</h2>
                  <button
                    type="button"
                    aria-expanded={formOpen}
                    onClick={() => setFormOpen((open) => !open)}
                    className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-caption font-bold hover:bg-surface-2"
                  >
                    Add a detail
                  </button>
                </div>
                {detailLabel ? (
                  <p className="mt-1 text-small">{detailLabel}</p>
                ) : (
                  <p className="mt-1 text-small text-ink-muted">No active detail.</p>
                )}
                {detailFailed && (
                  <p role="alert" className="mt-1 text-caption font-semibold text-error">
                    Could not create the detail. Check the dates; an employee cannot hold two details that overlap.
                  </p>
                )}
                {formOpen && (
                  <div className="mt-3">
                    <DetailForm onSave={saveDetail} onCancel={() => setFormOpen(false)} />
                  </div>
                )}
              </section>

              <section className="rounded-surface border border-line bg-canvas p-4">
                <h2 className="text-small font-bold">
                  {PAY_PERIOD.label} <span className="font-normal text-ink-muted">&middot; {PAY_PERIOD.range}</span>
                </h2>
                <ul className="mt-2 divide-y divide-line">
                  {days.map((day) => (
                    <li key={day.date} className="flex items-center gap-3 py-1.5 text-small">
                      <span className={`size-2.5 flex-none rounded-full ${CATEGORY_FILL[day.category]} border border-line`} />
                      <span className="w-24 flex-none font-bold">
                        {day.weekday} {day.month} {day.day}
                      </span>
                      <span>{formatScheduleLabel(day.label, format)}</span>
                      {day.detail && <span className="text-ink-muted">{day.detail}</span>}
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
